import { action, makeObservable } from 'mobx';
import { httpRequest } from '@/request';
import { message } from 'antd';
import { cache, debounce } from '@/utils';
import { enableStaticRendering } from 'mobx-react';
import { SERVER } from '@/request/config';
import StoreBase from './Base';

enableStaticRendering(typeof window === 'undefined');

class Tradeup extends StoreBase {
  constructor(props) {
    super(props);
    makeObservable(this);
  }

  /**
   * 创建以旧换新订单后set和进入产品页get，只有购物车有相关产品时才会返回信息
   * type:set / get（结算确认页） / getCache（产品页）/ remove
   */
  @action
  handleTradeupCache = ({ type, tradeup, cb }) => {
    if (type === 'get') {
      const { code } = this.root.area.country;
      const { orderCartItems, selectedCartItems } = this.root.order;
      const { isOrderConfirmPage, isCartPage } = this.root.app;
      // 区分结账页及购物车页数据源
      const cartItems = isOrderConfirmPage ? orderCartItems : selectedCartItems;
      const _tradeup = cache.get('tradeup') ? JSON.parse(cache.get('tradeup')) : '';
      // tradeup对应地区需有效
      if (_tradeup && code === _tradeup.countryCode && _tradeup.pid) {
        const hasTradeupProduct = cartItems.find(
          (o) => o.productId === _tradeup.pid && (isCartPage ? o.selected : true)
        );
        return hasTradeupProduct ? _tradeup : '';
      }
      cb && cb();
      return '';
    } else if (type === 'getCache') {
      const { code } = this.root.area.country;
      const _tradeup = cache.get('tradeup') ? JSON.parse(cache.get('tradeup')) : '';
      if (_tradeup && code === _tradeup.countryCode && _tradeup.pid) {
        return _tradeup;
      }
    } else if (type === 'set' && tradeup) {
      cache.set('tradeup', JSON.stringify(tradeup));
      cb && cb();
    } else if (type === 'remove') {
      cache.remove('tradeup');
      cb && cb();
    }
  };

  @action
  checkTradeupOrderState = debounce((cb) => {
    const _tradeup = this.handleTradeupCache({ type: 'get' });
    if (!_tradeup) {
      cb && cb(false);
      return;
    }
    this.getTradeupOrderDetail(
      {
        tradeupOrderNumber: _tradeup.orderNumber,
      },
      (res) => {
        if (res.code === 0) {
          const tradeUpOrder = res.data;
          if ([-1].includes(tradeUpOrder.state) || !tradeUpOrder.giftcard) {
            cb && cb(false);
          } else {
            if (tradeUpOrder.storeOrderNumber) {
              this.handleTradeupCache({ type: 'remove' });
              cb && cb(false);
              return;
            }
            this.handleTradeupCache({
              type: 'set',
              tradeup: {
                pid: _tradeup.pid,
                name: _tradeup.name,
                countryCode: tradeUpOrder.country,
                orderNumber: tradeUpOrder.tradeupOrderNumber,
                couponCode: tradeUpOrder.giftcard,
              },
            });
            cb && cb(true);
          }
          return;
        } else if (res.code === 40004 || res.code === 40005) {
          // 已取消或者其他
          this.handleTradeupCache({ type: 'remove' });
        }
        cb && cb(false);
      }
    );
  }, 500);

  @action
  getTradeupOrderDetail = (params, cb) => {
    const url = SERVER.API_SERVER_TRADEUP_Fresh + '/order/getByTradeupOrderInfo';
    httpRequest({
      url,
      params,
      instaToken: true,
    }).then((res) => {
      cb && cb(res);
      return res;
    });
  };

  /**
   * 获取用户可以选择 想要购买的相机类型
   */
  @action
  getCameraTypes = (cb) => {
    const url = SERVER.API_SERVER_TRADEUP_Fresh + '/info/getTypes';
    httpRequest({
      url,
      instaToken: true,
    }).then((res) => {
      cb && cb(res.code === 0 ? res.data?.types : []);
    });
  };

  // 获取用户手上旧相机的品牌list，获取后前端再动态填充数据
  @action
  getBrand = (params, cb) => {
    const url = SERVER.API_SERVER_TRADEUP_Fresh + '/info/getBrands';
    httpRequest({
      url,
      params,
    }).then((res) => {
      if (res.code === 0) {
        cb && cb(res.data.brands);
      }
    });
  };

  // 获取品牌对应的机器 ok
  @action
  getDevice = (params, cb) => {
    const url = SERVER.API_SERVER_TRADEUP_Fresh + '/info/getDevices';
    httpRequest({
      url,
      params,
    }).then((res) => {
      if (params.brand === 'Insta360') {
        res.data.devices = res.data.devices.filter((o) => o.name !== 'ONE X');
      }
      cb && cb(res);
    });
  };

  // 获取机器的估价规则 ok
  @action
  getRules = (device, cb) => {
    const url = SERVER.API_SERVER_TRADEUP_Fresh + '/info/getRules';
    httpRequest({
      url,
      params: {
        device,
      },
    }).then((res) => {
      cb && cb(res);
    });
  };

  // 创建估价订单
  @action
  createTradeUpOrder = (data, cb) => {
    const url = SERVER.API_SERVER_TRADEUP_Fresh + '/order/placeOrder';
    httpRequest({
      url,
      data,
      method: 'post',
      instaToken: true,
    }).then((res) => {
      cb && cb(res);
    });
  };

  // 校验tradeupAddress
  @action
  checkAddress = (data, cb) => {
    this.root.app.setGlobalValue('G_GlobalLoading', true);
    const url = SERVER.API_SERVER_TRADEUP_Fresh + '/order/checkAddress';
    httpRequest({
      url,
      data,
      method: 'post',
      instaToken: true,
    }).then(({ code, data }) => {
      if (code === 0) {
        const { is_valid, error } = data;
        if (is_valid) {
          cb && cb();
        } else if (!is_valid && error.length > 0) {
          error.forEach((err) => {
            message.warning(err.message);
          });
          this.root.app.setGlobalValue('G_GlobalLoading', false);
        } else {
          this.root.app.setGlobalValue('G_GlobalLoading', false);
        }
      } else {
        this.root.app.setGlobalValue('G_GlobalLoading', false);
      }
    });
  };

  // 校验tradeupAddress 根据地址信息生成shipping label
  @action
  backFillAddress = (data, cb) => {
    const url = SERVER.API_SERVER_TRADEUP_Fresh + '/order/createShippingLabel';
    httpRequest({
      url,
      data,
      method: 'post',
      instaToken: true,
    }).then((res) => {
      cb && cb(res);
      this.root.app.setGlobalValue('G_GlobalLoading', false);
    });
  };
}

export default Tradeup;
